import React, { useState, useEffect, useMemo } from 'react';
import {
  RiMenu3Line,
  RiCloseLine,
  RiArrowUpSLine,
  RiArrowDownSLine,
} from 'react-icons/ri';
import { SubjectSideNavigation, QuizContent } from '../components';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const Quiz = ({ subjectName, img }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState([]);
  const [modules, setModules] = useState([]);
  const [years, setYears] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);

  const [modulesLoading, setModulesLoading] = useState(false);
  const [yearsLoading, setYearsLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(false);

  const [showModuleMenu, setShowModuleMenu] = useState(false);

  const [isMdUp, setIsMdUp] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const sync = () => setIsMdUp(mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, []);

  const [filter, setFilter] = useState({
    module: '',
    year: '',
    subject: '',
    subjectId: '',
    questionType: '',
    random: 0,
    limit: 50,
    shuffle: true,
  });

  const [blockQuiz, setBlockQuiz] = useState(true);

  const [pageByKey, setPageByKey] = useState({
    random: 0,
    module: 0,
    year: 0,
    questionType: 0,
  });

  const optionKeyMap = {
    random: 'random',
    module: 'module',
    year: 'year',
    questionType: 'questionType',
  };

  const subjectId = selectedSubject?.[0]?.id;

  const formatQuestionTypeLabel = (type) => {
    const labels = {
      // The exam families a paper is set in — what students actually revise by.
      multiple_choice: 'Multiple Choice',
      short_answer: 'Short Answer',
      extended_response: 'Extended Response',
      essay: 'Extended Response',
      // Individual input types, kept so older links still render a sensible label.
      numeric_input: 'Short Answer',
      text_input: 'Short Answer (Text)',
      fill_in_the_blank: 'Fill in the Blank',
    };
    return labels[type] ?? String(type ?? '')
      .trim()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  useEffect(() => {
    const fetchSubject = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/subjects`, {
          params: {
            subjectName,
            examType: 'bgcse',
          },
        });

        setSelectedSubject(response.data || []);
      } catch (error) {
        console.error('Error fetching subject:', error);
        setSelectedSubject([]);
      }
    };

    fetchSubject();
  }, [subjectName]);

  useEffect(() => {
    const fetchModules = async () => {
      if (!subjectId) return;

      try {
        setModulesLoading(true);
        const response = await axios.get(`${API_BASE_URL}/subject_modules`, {
          params: { subjectId },
        });
        setModules(response.data || []);
      } catch (error) {
        console.error('Error fetching modules:', error);
        setModules([]);
      } finally {
        setModulesLoading(false);
      }
    };

    fetchModules();
  }, [subjectId]);

  useEffect(() => {
    const fetchYears = async () => {
      if (!subjectId) return;

      try {
        setYearsLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/questions/years`, {
          params: { subjectId, examType: 'bgcse' },
        });

        const rawYears = response.data || [];
        const sortedYearsDesc = [...rawYears].sort((a, b) => {
          const na = Number(a);
          const nb = Number(b);

          if (!Number.isNaN(na) && !Number.isNaN(nb)) return nb - na;
          return String(b).localeCompare(String(a));
        });

        setYears(sortedYearsDesc);
      } catch (error) {
        console.error('Error fetching years:', error);
        setYears([]);
      } finally {
        setYearsLoading(false);
      }
    };

    fetchYears();
  }, [subjectId]);

  useEffect(() => {
    const fetchQuestionTypes = async () => {
      if (!subjectId) return;

      try {
        setTypesLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/questions/question_types`, {
          params: { subjectId, examType: 'bgcse' },
        });

        const normalizedTypes = (response.data || []).map((type) => ({
          value: type,
          label: formatQuestionTypeLabel(type),
        }));

        setQuestionTypes(normalizedTypes);
      } catch (error) {
        console.error('Error fetching question types:', error);
        setQuestionTypes([]);
      } finally {
        setTypesLoading(false);
      }
    };

    fetchQuestionTypes();
  }, [subjectId]);

  const handleOptionClick = (option) => {
    setSelectedOption((prev) => (prev === option ? null : option));
    setPageByKey((p) => ({ ...p, [option]: 0 }));
  };

  const resetFilters = () => ({
    module: '',
    year: '',
    subject: '',
    subjectId: subjectId || '',
    questionType: '',
    random: 0,
    limit: 50,
    shuffle: true,
  });

  const isMultipleChoiceType = (val) => {
    const s = String(val ?? '').trim().toLowerCase();
    return (
      s === 'mc' ||
      s === 'mcq' ||
      s.includes('mcq') ||
      s.includes('multiple choice') ||
      s.includes('multiple-choice') ||
      s.includes('multiple_choice')
    );
  };

  const handleQuizSelect = (val) => {
    setBlockQuiz(false);

    const key = optionKeyMap[selectedOption];
    if (!key) return;

    if (key === 'questionType' && isMultipleChoiceType(val)) {
      setFilter(() => ({
        ...resetFilters(),
        questionType: val,
        limit: 50,
        shuffle: true,
        random: 50,
      }));
      return;
    }

    setFilter(() => ({
      ...resetFilters(),
      [key]: val,
    }));
  };

  const handleModuleSelect = () => {
    setShowModuleMenu(false);
  };

  // Each card carries its own accent so the four stop reading as one navy block. The
  // colour is decorative only — it never encodes a state the student has to interpret.
  const optionConfigs = useMemo(() => {
    const randomOpts = [5, 15, 25, 50].map((n) => ({
      label: `${n} Questions`,
      value: n,
    }));

    const moduleOpts = (modules || []).map((m) => ({
      label: m.module_name,
      value: m.id,
    }));

    const yearOpts = (years || []).map((y) => ({
      label: `${y}`,
      value: y,
    }));

    const typeOpts = questionTypes || [];

    return {
      random: {
        accent: '#2e92b0',
        title: 'Random Practice',
        subtitle: isMdUp ? 'Click to Begin' : 'Tap to Choose',
        loading: false,
        emptyText: 'No options.',
        options: randomOpts,
        parse: (v) => Number(v),
      },
      module: {
        accent: '#8b7fd4',
        title: 'Practice by Module',
        subtitle: isMdUp ? 'Click to Begin' : 'Tap to Choose',
        loading: modulesLoading,
        emptyText: 'No modules found.',
        options: moduleOpts,
        parse: (v) => Number(v),
      },
      year: {
        accent: '#d99a3e',
        title: 'Practice by Year',
        subtitle: isMdUp ? 'Click to Begin' : 'Tap to Choose',
        loading: yearsLoading,
        emptyText: 'No past paper years available.',
        options: yearOpts,
        parse: (v) => Number(v),
      },
      questionType: {
        accent: '#d4657f',
        title: 'Practice by Question',
        subtitle: isMdUp ? 'Click to Begin' : 'Tap to Choose',
        loading: typesLoading,
        emptyText: 'No question types available.',
        options: typeOpts,
        parse: (v) => v,
      },
    };
  }, [modules, years, questionTypes, modulesLoading, yearsLoading, typesLoading, isMdUp]);

  const orderedKeys = useMemo(() => ['random', 'module', 'year', 'questionType'], []);

  const SelectHeader = ({ text = 'Select to Start' }) => (
    <div className="text-xl sm:text-3xl font-thin py-3 sm:py-5">{text}</div>
  );

  const SelectItem = ({ children, onClick }) => (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className="my-1 font-thin text-xs sm:text-sm transition ease-in-out delay-80 hover:bg-white hover:bg-opacity-20 px-2 py-1"
    >
      {children}
    </div>
  );

  const PagedOptions = ({ optionKey }) => {
    const windowSize = 5;
    const cfg = optionConfigs[optionKey];
    const list = cfg?.options || [];

    const startRaw = pageByKey[optionKey] || 0;
    const maxStart = Math.max(0, list.length - windowSize);
    const start = Math.min(Math.max(0, startRaw), maxStart);
    const visible = list.slice(start, start + windowSize);

    const canUp = start > 0;
    const canDown = start < maxStart;

    const ArrowButton = ({ direction, disabled, onClick }) => (
      <button
        type="button"
        aria-label={direction === 'up' ? 'Previous options' : 'Next options'}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          onClick?.();
        }}
        className={`w-full flex items-center justify-center py-1 rounded-md transition backdrop-blur-sm
          ${disabled ? 'text-white/20 cursor-not-allowed' : 'text-white/70 hover:text-white hover:bg-white/10'}
        `}
      >
        {direction === 'up' ? <RiArrowUpSLine size={22} /> : <RiArrowDownSLine size={22} />}
      </button>
    );

    if (cfg.loading) return <div className="text-xs sm:text-sm opacity-80">Loading...</div>;
    if (list.length === 0) return <div className="text-xs sm:text-sm opacity-80">{cfg.emptyText}</div>;

    return (
      <div className="w-full">
        {list.length > windowSize && (
          <div className="mb-1">
            <ArrowButton
              direction="up"
              disabled={!canUp}
              onClick={() => setPageByKey((p) => ({ ...p, [optionKey]: start - 1 }))}
            />
          </div>
        )}

        <div className="relative">
          {visible.map((opt) => (
            <SelectItem
              key={`${optionKey}-${String(opt.value)}`}
              onClick={() => handleQuizSelect(opt.value)}
            >
              {opt.label}
            </SelectItem>
          ))}
        </div>

        {list.length > windowSize && (
          <div className="mt-1">
            <ArrowButton
              direction="down"
              disabled={!canDown}
              onClick={() => setPageByKey((p) => ({ ...p, [optionKey]: start + 1 }))}
            />
          </div>
        )}
      </div>
    );
  };

  const CardShell = ({ optionKey, onClick, active, children }) => {
    const cfg = optionConfigs[optionKey];

    if (!isMdUp) {
      if (active) {
        return (
          <div className="relative shadow-lg bg-[#1c394a] w-full transition-all duration-300 ring-2 ring-white/20 rounded-sm p-4 text-white">
            <div className="flex items-start justify-between gap-3">
              <div className="text-base font-thin text-left">
                <div>{cfg.title}</div>
                <div className="text-[11px] text-white/70 mt-1">{cfg.subtitle}</div>
              </div>

              <button
                type="button"
                aria-label="Close"
                className="text-white/70 hover:text-white transition"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOption(null);
                }}
              >
                <RiCloseLine size={22} />
              </button>
            </div>

            <div className="mt-3">
              {cfg.loading ? (
                <div className="text-xs opacity-80 text-left">Loading...</div>
              ) : cfg.options.length === 0 ? (
                <div className="text-xs opacity-80 text-left">{cfg.emptyText}</div>
              ) : (
                <select
                  className="w-full bg-white bg-opacity-95 text-gray-800 rounded-lg px-3 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  defaultValue=""
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (!raw) return;
                    const parsed = cfg.parse(raw);
                    handleQuizSelect(parsed);
                  }}
                >
                  <option value="" disabled>
                    Select an option…
                  </option>
                  {cfg.options.map((opt) => (
                    <option key={`${optionKey}-${String(opt.value)}`} value={String(opt.value)}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        );
      }

      return (
        <button
          type="button"
          onClick={onClick}
          className="relative overflow-hidden shadow-lg bg-[#1c394a] w-full transition-all duration-300 flex flex-col items-center justify-center px-3 py-5 text-white rounded-sm ring-1 ring-white/10"
        >
          <div className="absolute inset-x-0 top-0 h-[3px]" style={{ backgroundColor: cfg.accent }} />
          <div className="text-sm sm:text-base font-thin text-center">{cfg.title}</div>
          <div className="text-[10px] sm:text-xs text-white/70 mt-1">{cfg.subtitle}</div>
          <div
            className="absolute inset-0 opacity-0 hover:opacity-25 transition-opacity duration-300 ease-in-out"
            style={{ backgroundImage: `linear-gradient(to right, #133042, ${cfg.accent}, #133042)` }}
          />
        </button>
      );
    }

    return (
      <div
        onClick={onClick}
        className={`text-xl sm:text-2xl shadow-lg hover:cursor-pointer relative overflow-hidden bg-[#1c394a] w-full mx-1 sm:mx-2 transition-all duration-300 ${
          active ? 'py-4' : 'py-10 sm:py-20'
        }`}
      >
        {active ? (
          <div className="relative w-full min-h-[300px] sm:min-h-[400px] max-h-[520px] flex flex-col px-2 pb-4 overflow-y-auto">
            {children}
          </div>
        ) : (
          <>
            <div
              className="absolute inset-0 opacity-0 hover:opacity-40 transition-opacity duration-300 ease-in-out z-10"
              style={{ backgroundImage: `linear-gradient(to right, #133042, ${cfg.accent}, #133042)` }}
            />
            <div className="absolute inset-x-0 top-0 h-[3px] z-10" style={{ backgroundColor: cfg.accent }} />
            <div className="font-thin">{cfg.title}</div>
            <div className="text-sm sm:text-lg w-full text-white mt-[50%]">{cfg.subtitle}</div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen h-full">
      <div className="grid grid-cols-1 lg:grid-cols-5 min-h-screen h-full">
        <div className="hidden lg:block lg:col-span-1 bg-white text-sm">
          <SubjectSideNavigation setSelectedModule={0} subjectName={subjectName} img={img} />
        </div>

        <div className="col-span-1 lg:col-span-4 bg-[#1c394a] bg-opacity-50 flex flex-col text-center">
          <div className="lg:hidden p-4">
            <button
              onClick={() => setShowModuleMenu(!showModuleMenu)}
              className="w-full bg-white rounded-lg shadow-sm px-4 py-3 flex items-center justify-between text-gray-700 hover:bg-gray-50 transition"
            >
              <div className="flex items-center gap-3">
                <img src={img} alt={subjectName} className="h-8 w-8 rounded object-cover" />
                <span className="font-semibold">{subjectName} Navigation</span>
              </div>
              {showModuleMenu ? (
                <RiCloseLine size={24} className="text-gray-600" />
              ) : (
                <RiMenu3Line size={24} className="text-gray-600" />
              )}
            </button>

            {showModuleMenu && (
              <div className="mt-2 bg-white rounded-lg shadow-lg overflow-hidden animate-scale-down">
                <SubjectSideNavigation
                  setSelectedModule={handleModuleSelect}
                  subjectName={subjectName}
                  img={img}
                />
              </div>
            )}
          </div>

          {blockQuiz ? (
            <>
              <div className="pt-6 sm:pt-10 px-4">
                <p className="text-[11px] sm:text-xs font-medium uppercase tracking-[0.22em] text-[#7fd4ea]">
                  Practice
                </p>
                <h2 className="mt-2 text-white font-thin text-3xl sm:text-5xl mb-6 sm:mb-10">
                  {subjectName}
                </h2>
                <h2 className="pt-3 sm:pt-5 text-white/60 font-thin text-lg sm:text-xl">
                  Select Quiz Option to Start
                </h2>
              </div>

              <div className="font-thin w-full flex flex-col md:flex-row md:h-[50%] pt-5 px-4 md:px-5 text-white gap-3 md:gap-0">
                {orderedKeys.map((key) => (
                  <CardShell
                    key={key}
                    optionKey={key}
                    onClick={() => handleOptionClick(key)}
                    active={selectedOption === key}
                  >
                    <SelectHeader />
                    <PagedOptions optionKey={key} />
                  </CardShell>
                ))}
              </div>
            </>
          ) : (
            <QuizContent filter={filter} />
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes scale-down {
          0% {
            transform: scaleY(0);
            opacity: 0;
            transform-origin: top;
          }
          100% {
            transform: scaleY(1);
            opacity: 1;
            transform-origin: top;
          }
        }
        .animate-scale-down {
          animation: scale-down 0.2s ease-out both;
        }
      `}</style>
    </div>
  );
};

export default Quiz;